import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import useAppSelector from "@insite/client-framework/Common/Hooks/useAppSelector";
import siteMessage from "@insite/client-framework/SiteMessage";
import translate from "@insite/client-framework/Translate";
import { WarehouseModel } from "@insite/client-framework/Types/ApiModels";
import AddressInfoDisplay, { AddressInfoDisplayStyles } from "@insite/content-library/Components/AddressInfoDisplay";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import VisuallyHidden from "@insite/mobius/VisuallyHidden";
import getColor from "@insite/mobius/utilities/getColor";
import { getIsUkWebsite } from "@tSurya/Common/Utilities/websiteUtils";
import InfoCircleIcon from "@tSurya/Icons/InfoCircleIcon";
import { COLORS, TYPOGRAPHY } from "@tSurya/theme/design";
import React from "react";
import { css } from "styled-components";

interface OwnProps {
    location: WarehouseModel;
    onEdit: () => void;
    extendedStyles?: PickUpLocationAddressInfoDisplayStyles;
}

export interface PickUpLocationAddressInfoDisplayStyles {
    container?: GridContainerProps;
    headingGridItem?: GridItemProps;
    headingText?: TypographyPresentationProps;
    editLink?: LinkPresentationProps;
    addressGridItem?: GridItemProps;
    locationNameText?: TypographyPresentationProps;
    address?: AddressInfoDisplayStyles;
    scheduleNotice?: TypographyPresentationProps;
}

export const pickUpLocationAddressInfoDisplayStyles: PickUpLocationAddressInfoDisplayStyles = {
    container: {
        gap: 0,
        css: css`
            border: 1px solid transparent !important;
        `,
    },
    headingGridItem: {
        width: 12,
        css: css`
            padding: 10px;
            border-bottom: 1px solid ${getColor("common.border")};
            position: relative;
        `,
    },
    headingText: {
        weight: TYPOGRAPHY.weight.bold,
        css: css`
            font-size: ${TYPOGRAPHY.size.base};
            line-height: ${TYPOGRAPHY.lineHeight.xbase};
            margin: 0 1rem 0 0;
        `,
    },
    editLink: {
        css: css`
            display: flex;
            min-width: 55px;
            margin-left: auto;
            > span {
                display: flex;
            }
        `,
    },
    addressGridItem: {
        width: 12,
        css: css`
            padding: 10px;
            flex-direction: column;
        `,
    },
    locationNameText: {
        css: css`
            display: block;
            color: ${COLORS.text.main};
        `,
    },
    scheduleNotice: {
        css: css`
            display: block;
            margin-top: 8px;
            font-size: ${TYPOGRAPHY.size.smaller};
            line-height: ${TYPOGRAPHY.lineHeight.base};
            color: ${COLORS.text.main};

            svg {
                vertical-align: text-bottom;
                margin-right: 0.375em;
            }
        `,
    },
};

const PickUpLocationAddressInfoDisplay = ({ location, onEdit, extendedStyles }: OwnProps) => {
    const styles = mergeToNew(pickUpLocationAddressInfoDisplayStyles, extendedStyles);
    const isUkWebsite = useAppSelector(getIsUkWebsite);
    return (
        <GridContainer {...styles.container} data-test-selector="pickUpLocationAddressInfoDisplay_container">
            <GridItem {...styles.headingGridItem}>
                <Typography {...styles.headingText}>{translate("Pick Up Location")}</Typography>
                {/* Outside the UK the location is derived from the cart's brand, so there is nothing to edit. */}
                {isUkWebsite && (
                    <Link {...styles.editLink} onClick={onEdit}>
                        {translate("Edit")}
                        <VisuallyHidden>{translate("Pick Up Location")}</VisuallyHidden>
                    </Link>
                )}
            </GridItem>
            <GridItem {...styles.addressGridItem}>
                {isUkWebsite ? (
                    <Typography {...styles.locationNameText}>{siteMessage("PickUpfromWarehouse")}</Typography>
                ) : (
                    <>
                        <Typography {...styles.locationNameText}>
                            {location?.description || location?.name}
                        </Typography>
                        <AddressInfoDisplay {...location} extendedStyles={styles.address} />
                        {/* Pickup must be scheduled; the address alone could suggest a customer can just walk in. */}
                        <Typography {...styles.scheduleNotice}>
                            <InfoCircleIcon />
                            {translate(
                                "Pickup must be scheduled in advance. Please contact Customer Support at 877.275.7847 or info@suryainc.com to arrange your pickup time.",
                            )}
                        </Typography>
                    </>
                )}
            </GridItem>
        </GridContainer>
    );
};

export default PickUpLocationAddressInfoDisplay;
